import type { ReportFilter } from './model';
import { ReportDimension, ReportMetric } from './reportEnums';

const scopedDimensions = new Set([
  ReportDimension.Brand,
  ReportDimension.Order,
  ReportDimension.LineItem,
  ReportDimension.Creative,
  ReportDimension.CrId,
  ReportDimension.Advertiser,
  ReportDimension.Agency,
]);
const scopeFilters = [
  'msBrandIds',
  'msCampaignIds',
  'msLineItemIds',
  'msCreativeIds',
  'advertiserIds',
  'agencyIds',
] as const;
const dimensions = new Set([
  ReportDimension.Video,
  ReportDimension.Marketplace,
  ...scopedDimensions,
  ReportDimension.Day,
  ReportDimension.Month,
  ReportDimension.Year,
  ReportDimension.Hour,
  ReportDimension.Publisher,
  ReportDimension.Placement,
  ReportDimension.Network,
  ReportDimension.App,
  ReportDimension.Application,
  ReportDimension.AppStore,
  ReportDimension.PublisherDomain,
  ReportDimension.ContentType,
  ReportDimension.Platform,
  ReportDimension.DemandPartner,
  ReportDimension.DemandIntegration,
  ReportDimension.DemandPartnerOwner,
  ReportDimension.BidderCode,
  ReportDimension.Deal,
  ReportDimension.AuctionType,
  ReportDimension.Adomain,
  ReportDimension.SchainAsi,
  ReportDimension.LrSegment,
  ReportDimension.Country,
  ReportDimension.Region,
  ReportDimension.DataCenter,
  ReportDimension.Xp,
  ReportDimension.Xp1,
  ReportDimension.Xp2,
  ReportDimension.Xp3,
]);
const metrics = new Set([
  ReportMetric.Ecpm,
  ReportMetric.RevenueBids,
  ReportMetric.RevenueFill,
  ReportMetric.PreMpRevenue,
  ReportMetric.AdImpressionsNonStitched,
  ReportMetric.FilledImpressionsNonStitched,
  ReportMetric.Marketplace,

  ReportMetric.Bids,
  ReportMetric.WonBids,
  ReportMetric.LostBids,
  ReportMetric.BidsLi,
  ReportMetric.WonBidsLi,
  ReportMetric.WinRatio,
  ReportMetric.LostRatio,
  ReportMetric.BidWinLostRatio,
  ReportMetric.FilledImpressions,
  ReportMetric.FilledImpressionsLi,
  ReportMetric.AdImpressions,
  ReportMetric.AdImpressionsLi,
  ReportMetric.RenderRate,
  ReportMetric.RenderRateLi,
  ReportMetric.InsertionRate,
  ReportMetric.BidFillRatio,
  ReportMetric.FilledImpressionsCache,
  ReportMetric.AdImpressionsCache,
  ReportMetric.FilledImpressionsStitched,
  ReportMetric.AdImpressionsStitched,
  ReportMetric.AdImpressionsDvIqm,
  ReportMetric.AdClicks,
  ReportMetric.Ctr,
  ReportMetric.View,
  ReportMetric.ViewPct,
  ReportMetric.AdStart,
  ReportMetric.AdQuartile1,
  ReportMetric.AdMid,
  ReportMetric.AdQuartile3,
  ReportMetric.AdCompletion,
  ReportMetric.Revenue,
  ReportMetric.RevenueLi,
  ReportMetric.Rpm,
  ReportMetric.RpmLi,
  ReportMetric.RpmFill,
  ReportMetric.BidCpm,
  ReportMetric.FillCpm,
  ReportMetric.ClickCpm,
  ReportMetric.RevenueCache,
  ReportMetric.RevenueStitched,
  ReportMetric.MpRevenue,
  ReportMetric.PubRevenue,
  ReportMetric.PubRevenueLi,
  ReportMetric.RpmNet,
  ReportMetric.Profit,
  ReportMetric.MarginPct,
  ReportMetric.NetworkRevenue,
  ReportMetric.NetworkProfit,
  ReportMetric.NetworkSaasType,
  ReportMetric.NetworkSaasValue,
  ReportMetric.NetworkSaasMetric,
  ReportMetric.NetworkSaasFee,
  ReportMetric.LrSegmentCost,
  ReportMetric.BcFind,
  ReportMetric.BcStore,
  ReportMetric.BcUnlock,
  ReportMetric.BcUse,
  ReportMetric.Day,
  ReportMetric.Hour,
  ReportMetric.Month,
  ReportMetric.Year,
  ReportMetric.BrandId,
  ReportMetric.BrandName,
  ReportMetric.Order,
  ReportMetric.LineItem,
  ReportMetric.LineItemId,
  ReportMetric.CreativeId,
  ReportMetric.CreativeName,
  ReportMetric.CrId,
  ReportMetric.AdvertiserId,
  ReportMetric.AdvertiserName,
  ReportMetric.AgencyId,
  ReportMetric.AgencyName,
  ReportMetric.PublisherId,
  ReportMetric.PublisherName,
  ReportMetric.PublisherSecondaryId,
  ReportMetric.PlacementId,
  ReportMetric.Placement,
  ReportMetric.NetworkId,
  ReportMetric.NetworkName,
  ReportMetric.AppId,
  ReportMetric.AppName,
  ReportMetric.AppBundleId,
  ReportMetric.ApplicationId,
  ReportMetric.ApplicationName,
  ReportMetric.AppStoreId,
  ReportMetric.AppStoreUrl,
  ReportMetric.Domain,
  ReportMetric.VideoId,
  ReportMetric.VideoName,
  ReportMetric.Video,
  ReportMetric.ContentType,
  ReportMetric.PlatformId,
  ReportMetric.Platform,
  ReportMetric.DemandPartnerId,
  ReportMetric.DemandPartnerName,
  ReportMetric.DemandIntegrationId,
  ReportMetric.DemandIntegration,
  ReportMetric.DemandPartnerOwnerId,
  ReportMetric.DemandPartnerOwnerName,
  ReportMetric.BidderCode,
  ReportMetric.Deal,
  ReportMetric.AuctionType,
  ReportMetric.Adomain,
  ReportMetric.SchainAsi,
  ReportMetric.LrSegment,
  ReportMetric.LrSegmentName,
  ReportMetric.Country,
  ReportMetric.Region,
  ReportMetric.DataCenter,
  ReportMetric.Xp,
  ReportMetric.Xp1,
  ReportMetric.Xp2,
  ReportMetric.Xp3,
]);

// null leaves the existing non-MadSense compatibility rules in control.
export function madSenseSelectionCompatible(
  selectedDimensions: readonly ReportDimension[],
  selectedMetrics: readonly ReportMetric[],
  filters: Partial<ReportFilter> = {},
): boolean | null {
  const active =
    selectedDimensions.some((dimension) => scopedDimensions.has(dimension)) ||
    scopeFilters.some((field) => Array.isArray(filters[field]) && filters[field].length > 0);
  if (!active) return null;
  return (
    selectedDimensions.every((dimension) => dimensions.has(dimension)) &&
    selectedMetrics.every((metric) => metrics.has(metric))
  );
}
